import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ClassDetailsNoSectionsQuery,
  ClassDetailsQuery_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useIsMobile, useScrollToElement } from "@outschool/ui-utils";
import React, { SetStateAction, useRef, useState } from "react";

import { useStickyHeader } from "../../../hooks/useStickyHeader";
import { TabConfigType } from "../subcomponents/ClassDetailsNav";
import { TabValue } from "../subcomponents/PublicReviewsOfActivity";
import DesktopContainer from "./DesktopContainer";
import MobileContainer from "./MobileContainer";

interface ClassDetailsMainBodyProps {
  activity: ClassDetailsQuery_ActivityFragment;
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
  isAvailabilityModalOpen: boolean;
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  onClickMoreLikeThis: () => void;
  onClickViewTeacher: (event: React.SyntheticEvent) => void;
  setIsAvailabilityModalOpen: React.Dispatch<SetStateAction<boolean>>;
  viewAvailabilityRef: React.RefObject<HTMLElement>;
  viewTheTeacherRef: React.RefObject<HTMLElement>;
}

interface RefsContainer {
  root: React.RefObject<HTMLElement>;
  about: {
    header: React.RefObject<HTMLElement>;
    overview: React.RefObject<HTMLElement>;
    classExperience: React.RefObject<HTMLElement>;
  };
  learningOverview: {
    learningGoals: React.RefObject<HTMLElement>;
    syllabus: React.RefObject<HTMLElement>;
    otherDetails: React.RefObject<HTMLElement>;
  };
  schedule: {
    sections: React.RefObject<HTMLElement>;
    availability: React.RefObject<HTMLElement>;
  };
  teacher: {
    header: React.RefObject<HTMLElement>;
    expertise: React.RefObject<HTMLElement>;
  };
  reviews: React.RefObject<HTMLElement>;
}

interface BaseContainerProps {
  activity: ClassDetailsQuery_ActivityFragment;
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
  classNavTabs?: TabConfigType[];
  isAvailabilityModalOpen?: boolean;
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  onClickMoreLikeThis: () => void;
  onClickViewReviews?: () => void;
  refsContainer: RefsContainer;
  setIsAvailabilityModalOpen?: React.Dispatch<SetStateAction<boolean>>;
  setTabValue?: React.Dispatch<React.SetStateAction<TabValue>>;
  tabValue?: TabValue;
}

export interface DesktopContainerProps extends BaseContainerProps {
  onClickViewTeacher?: (event: React.SyntheticEvent) => void;
  viewAvailabilityRef: React.RefObject<HTMLElement>;
}

export interface MobileContainerProps extends BaseContainerProps {}

export default function ClassDetailsMainBody({
  activity,
  activityWithEnrollableTimes,
  isAvailabilityModalOpen = false,
  leader,
  onClickMoreLikeThis,
  onClickViewTeacher,
  setIsAvailabilityModalOpen,
  viewAvailabilityRef,
  viewTheTeacherRef,
}: ClassDetailsMainBodyProps) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\ClassDetailsMainBody\\index",
  );
  const isMobile = useIsMobile();
  const { headerHeight: mainHeaderHeight } = useStickyHeader();
  const scrollToElement = useScrollToElement({
    desktopOffset: mainHeaderHeight + 80,
  });
  const [tabValue, setTabValue] = useState(TabValue.Class);

  const refsContainer = {
    root: useRef<HTMLElement>(null),
    about: {
      header: useRef<HTMLElement>(null),
      overview: useRef<HTMLElement>(null),
      classExperience: useRef<HTMLElement>(null),
    },
    learningOverview: {
      learningGoals: useRef<HTMLElement>(null),
      syllabus: useRef<HTMLElement>(null),
      otherDetails: useRef<HTMLElement>(null),
    },
    schedule: {
      sections: useRef<HTMLElement>(null),
      availability: viewAvailabilityRef,
    },
    teacher: {
      header: viewTheTeacherRef,
      expertise: useRef<HTMLElement>(null),
    },
    reviews: useRef<HTMLElement>(null),
  };

  const classNavTabs = [
    {
      label: t("Overview"),
      value: "about",
      ref: Object.values(refsContainer.about),
    },
    {
      label: t("Learning Overview"),
      value: "learning-overview",
      ref: Object.values(refsContainer.learningOverview),
    },
    {
      label: t("Schedule"),
      value: "schedule",
      ref: Object.values(refsContainer.schedule),
    },
    {
      label: t("Teacher"),
      value: "teacher",
      ref: Object.values(refsContainer.teacher),
    },
    { label: t("Reviews"), value: "reviews", ref: refsContainer.reviews },
  ];

  React.useEffect(() => {
    if (window.location.search.includes("teacher")) {
      scrollToElement(refsContainer.teacher.header.current);
    } else if (window.location.hash === "#availability") {
      scrollToElement(refsContainer.schedule.sections.current);
    }
  });

  const onClickViewReviews = React.useCallback(() => {
    setTabValue(TabValue.Leader); // switch to teacher reviews tab
    scrollToElement(refsContainer.reviews.current);
  }, [scrollToElement, refsContainer.reviews]);

  const baseContainerProps: BaseContainerProps = {
    activity,
    activityWithEnrollableTimes,
    classNavTabs,
    isAvailabilityModalOpen,
    leader,
    onClickMoreLikeThis,
    onClickViewReviews,
    refsContainer,
    setIsAvailabilityModalOpen,
    setTabValue,
    tabValue,
  };

  const mobileContainerProps: MobileContainerProps = {
    ...baseContainerProps,
  };

  const desktopContainerProps: DesktopContainerProps = {
    ...baseContainerProps,
    onClickViewTeacher,
    viewAvailabilityRef,
  };

  return isMobile ? (
    <MobileContainer {...mobileContainerProps} />
  ) : (
    <DesktopContainer {...desktopContainerProps} />
  );
}
